import React, { useEffect, useState } from "react";
import client from "../../client/Client";
import HeaderBanner from "../ui/HeaderBanner";
import { useParams } from "react-router-dom";
import "../styles/TechnicalProg.css";

interface FileAsset {
  asset?: {
    url?: string;
  };
}

interface DataArrayItem {
  Title: string;
  file?: FileAsset;
}

interface MyData {
  YearVersion: string;
  CommunicationSignalProcessingInformationTechnology: DataArrayItem[];
  PowerSystemsSmartEnergies: DataArrayItem[];
  SystemsAutomationControl: DataArrayItem[];
  SensorsCircuitsandInstrumentationSystems: DataArrayItem[];

  OpeningPL1?: FileAsset;
  PL2KLSAC1?: FileAsset;
  PL3?: FileAsset;
  PL4?: FileAsset;
  PL5KLSAC2?: FileAsset;
  PhD3M?: FileAsset;

  SSDAbstractBook?: FileAsset;
  SSDProgramBook?: FileAsset;
  SSDSocialProgram?: FileAsset;
  WS1SSDmeeting?: FileAsset;
  WS2closure?: FileAsset;
}

const TechnicalProg = () => {
  const params = useParams<{ version: string }>();

  const [data, setData] = useState<MyData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!params.version) {
      setLoading(false);
      return;
    }

    const query = `*[_type == "TechnicalProgram" && YearVersion == $version][0]{
      YearVersion,

      OpeningPL1{
        asset->{
          url
        }
      },
      PL2KLSAC1{
        asset->{
          url
        }
      },
      PL3{
        asset->{
          url
        }
      },
      PL4{
        asset->{
          url
        }
      },
      PL5KLSAC2{
        asset->{
          url
        }
      },
      PhD3M{
        asset->{
          url
        }
      },

      CommunicationSignalProcessingInformationTechnology[]{
        Title,
        file{
          asset->{
            url
          }
        }
      },

      PowerSystemsSmartEnergies[]{
        Title,
        file{
          asset->{
            url
          }
        }
      },

      SystemsAutomationControl[]{
        Title,
        file{
          asset->{
            url
          }
        }
      },

      SensorsCircuitsandInstrumentationSystems[]{
        Title,
        file{
          asset->{
            url
          }
        }
      },

      SSDAbstractBook{
        asset->{
          url
        }
      },
      SSDProgramBook{
        asset->{
          url
        }
      },
      SSDSocialProgram{
        asset->{
          url
        }
      },
      WS1SSDmeeting{
        asset->{
          url
        }
      },
      WS2closure{
        asset->{
          url
        }
      }
    }`;

    setLoading(true);

    client
      .fetch(query, { version: params.version })
      .then((result: MyData | null) => {
        setData(result);
      })
      .catch((error) => {
        console.error("Error fetching data:", error);
        setData(null);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [params.version]);

  const hasFile = (fileAsset?: FileAsset) => !!fileAsset?.asset?.url;

  const hasAnyPlenaryFile =
    hasFile(data?.OpeningPL1) ||
    hasFile(data?.PL2KLSAC1) ||
    hasFile(data?.PL3) ||
    hasFile(data?.PL4) ||
    hasFile(data?.PL5KLSAC2) ||
    hasFile(data?.PhD3M);

  const hasAnyBottomFile =
    hasFile(data?.SSDAbstractBook) ||
    hasFile(data?.SSDProgramBook) ||
    hasFile(data?.SSDSocialProgram) ||
    hasFile(data?.WS1SSDmeeting) ||
    hasFile(data?.WS2closure);

  const FileButton = ({
    fileAsset,
    text,
    className = "",
  }: {
    fileAsset?: FileAsset;
    text: string;
    className?: string;
  }) => {
    if (!hasFile(fileAsset)) return null;

    return (
      <a
        href={fileAsset?.asset?.url}
        target="_blank"
        rel="noopener noreferrer"
        className={`bg-white border-2 border-blue-901 rounded-lg p-6 shadow-md hover:shadow-lg transition-all duration-300 hover:bg-blue-901 group text-center block ${className}`}
      >
        <span className="text-blue-901 group-hover:text-white font-semibold text-lg">
          {text}
        </span>
      </a>
    );
  };

  const TABLE_HEAD_1 = [
    "Communication, Signal Processing & Information Technology (CSP)",
    "Power Systems & Smart Energies (PSE)",
  ];

  const TABLE_HEAD_2 = [
    "Systems, Automation & Control (SAC)",
    "Sensors, Circuits and Instrumentation Systems (SCI)",
  ];

  const renderTableCell = (
    item?: DataArrayItem,
    alternateBackground = false
  ) => {
    const tdClass = `p-0 text-xs sm:text-sm border-b border-gray-300 ${
      alternateBackground ? "bg-gray-100" : "bg-white"
    }`;

    if (!item) {
      return <td className={tdClass}></td>;
    }

    if (item.file?.asset?.url) {
      return (
        <td className={tdClass}>
          <a
            href={item.file.asset.url}
            target="_blank"
            rel="noopener noreferrer"
            className="block w-full h-full px-4 py-6 text-blue-600 hover:bg-blue-901 hover:text-white whitespace-normal break-words transition font-medium"
          >
            {item.Title}
          </a>
        </td>
      );
    }

    return (
      <td className={tdClass}>
        <div className="block w-full h-full px-4 py-6 text-gray-700 whitespace-normal break-words font-medium">
          {item.Title}
        </div>
      </td>
    );
  };

  const renderTwoColumnTable = (
    leftData: DataArrayItem[] = [],
    rightData: DataArrayItem[] = [],
    headers: string[]
  ) => {
    const maxRows = Math.max(leftData.length, rightData.length);

    if (maxRows === 0) return null;

    return (
      <table className="rounded-t-lg w-full mx-auto bg-gray-200 text-gray-800 mt-6">
        <thead>
          <tr>
            {headers.map((head) => (
              <th
                key={head}
                className="px-4 py-5 text-left border-b-2 border-gray-300 font-semibold w-1/2"
              >
                {head}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {Array.from({ length: maxRows }).map((_, index) => {
            const leftItem = leftData[index];
            const rightItem = rightData[index];

            return (
              <tr key={index}>
                {renderTableCell(leftItem, false)}
                {renderTableCell(rightItem, true)}
              </tr>
            );
          })}
        </tbody>
      </table>
    );
  };

  if (loading) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-24">
        <div
          className="fixed left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none -z-10"
          aria-hidden="true"
        >
          <svg width="1360" height="578" viewBox="0 0 1360 578" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="illustration-01">
                <stop stopColor="#FFF" offset="0%" />
                <stop stopColor="#EAEAEA" offset="77.402%" />
                <stop stopColor="#DFDFDF" offset="100%" />
              </linearGradient>
            </defs>
            <g fill="url(#illustration-01)" fillRule="evenodd">
              <circle cx="1232" cy="128" r="128" />
              <circle cx="155" cy="443" r="64" />
            </g>
          </svg>
        </div>

        <HeaderBanner title="Technical Program" />
        <div className="p-4 text-center text-lg">Loading...</div>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-24">
        <div
          className="fixed left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none -z-10"
          aria-hidden="true"
        >
          <svg width="1360" height="578" viewBox="0 0 1360 578" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="illustration-01">
                <stop stopColor="#FFF" offset="0%" />
                <stop stopColor="#EAEAEA" offset="77.402%" />
                <stop stopColor="#DFDFDF" offset="100%" />
              </linearGradient>
            </defs>
            <g fill="url(#illustration-01)" fillRule="evenodd">
              <circle cx="1232" cy="128" r="128" />
              <circle cx="155" cy="443" r="64" />
            </g>
          </svg>
        </div>

        <HeaderBanner title="Technical Program" />
        <div className="p-4 text-center text-red-500">
          No technical program found for this version.
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 pt-32 pb-24">
      <div
        className="fixed left-1/2 transform -translate-x-1/2 bottom-0 pointer-events-none -z-10"
        aria-hidden="true"
      >
        <svg width="1360" height="578" viewBox="0 0 1360 578" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <linearGradient x1="50%" y1="0%" x2="50%" y2="100%" id="illustration-01">
              <stop stopColor="#FFF" offset="0%" />
              <stop stopColor="#EAEAEA" offset="77.402%" />
              <stop stopColor="#DFDFDF" offset="100%" />
            </linearGradient>
          </defs>
          <g fill="url(#illustration-01)" fillRule="evenodd">
            <circle cx="1232" cy="128" r="128" />
            <circle cx="155" cy="443" r="64" />
          </g>
        </svg>
      </div>

      <HeaderBanner title="Technical Program" />

      <div className="text-left space-y-8 pt-12">
        {hasAnyPlenaryFile && (
          <div className="space-y-4">
            <h1 className="text-2xl font-semibold text-red-400">Plenaries</h1>

            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
              <FileButton fileAsset={data.OpeningPL1} text="Opening PL 1 File" />
              <FileButton fileAsset={data.PL2KLSAC1} text="PL 2 KL SAC1 File" />
              <FileButton fileAsset={data.PL3} text="PL 3 File" />
              <FileButton fileAsset={data.PL4} text="PL 4 File" />
              <FileButton fileAsset={data.PL5KLSAC2} text="PL5KLSAC2 File" />
              <FileButton fileAsset={data.PhD3M} text="PhD3M File" />
            </div>
          </div>
        )}

        {renderTwoColumnTable(
          data.CommunicationSignalProcessingInformationTechnology,
          data.PowerSystemsSmartEnergies,
          TABLE_HEAD_1
        )}

        {renderTwoColumnTable(
          data.SystemsAutomationControl,
          data.SensorsCircuitsandInstrumentationSystems,
          TABLE_HEAD_2
        )}

        {hasAnyBottomFile && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            <FileButton fileAsset={data.SSDAbstractBook} text="SSD-Abstract Book" />
            <FileButton fileAsset={data.SSDProgramBook} text="SSD-Program Book" />
            <FileButton fileAsset={data.SSDSocialProgram} text="SSD-Social Program" />
            <FileButton fileAsset={data.WS1SSDmeeting} text="WS-1–SSD meeting" />
            <FileButton fileAsset={data.WS2closure} text="WS-2–closure" />
          </div>
        )}
      </div>
    </div>
  );
};

export default TechnicalProg;